import ModalCreateEdit from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { useTypeIdNumber } from '../../../../../../../utils/hooks/useTypeIdNumber'
import { getActiveLangs, getAllClientManagerEmployees, getConfigParam, getDictionaryCountries } from '../../../../../../../utils/reducers/getters'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { addDictionaryExtraLine, dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { CustomerModelTypeAndAddress } from '../../../../../../../specificComponents/Customers/types'
import useCreateCustomerAndAddress from '../../../../../../../utils/hooks/customer/useCreateCustomerAndAddress'
import { CustomerGroupPayload } from '../../../../../../../utils/reducers/customers/customerGroups'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { sortCountries } from '../../../../../../../utils/globals/sortCountries'

type ModalCreateCustomerProps = ModalComponentProps & ReduxModalCreateCustomer & {}

const ModalCreateCustomer: React.FC<ModalCreateCustomerProps> = ({ customerGroups, employees,...rest}) => {
    const { t } = useTwinTranslation()
    const { typesIdNumber } = useTypeIdNumber()
    const { dictCustomerOrigins, createAddress, handleShowCreateAddress, myEmployees } = useMocalCreateCustomerLogic({ employees})
    const { addressFields } = useCreateCustomerAndAddress({ createAddress })
    const fields: FormRender<CustomerModelTypeAndAddress> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'surname',
                    label: t('surname', 'Apellidos'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'fiscal_name',
                    label: t('fiscalName', 'Nombre fiscal'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'code',
                    label: t('customerCode', 'Código de Cliente'),
                    component: 'InputWithLabelMarginIntegerStateFull',
                },
                {
                    name: 'type_id_number',
                    label: t('typeIdNumber', 'Tipo de documento identificación'),
                    component: 'CustomSelect',
                    items: typesIdNumber,
                    value: 0
                },
                {
                    name: 'id_number',
                    label: t('id_number', 'DNI'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'ResponsibleEmployeeId',
                    label: t('ourManager', 'Nuestro gestor'),
                    component: 'CustomSelectWithSearchBar',
                    items: myEmployees || {},
                    fieldName: 'fullname_short',
                    sortItems: true
                },
                {
                    name: 'nationality',
                    label: t('nationality', 'Nacionalidad'),
                    component: 'CustomSelectWithSearchBar',
                    items: getDictionaryCountries(),
                    sortFunction: sortCountries,
                },
                {
                    name: 'doVatVerification',
                    component: 'CheckboxMargin',
                    label: t('checkIDNumber', 'Verificar el CIF'),
                    defaultChecked: true
                },
                {
                    name: 'notes',
                    label: t('notes', 'Notas'),
                    component: 'TextArea',
                    parentClassName: returnClassOfSpanTemplate(2)
                },

            ]
        },
        {
            cols: 2,
            title: t('contactInfo', 'Información de contacto'),
            elements: [
                {
                    name: 'customerContactName',
                    label: t('customerContactName', 'Nombre persona de contacto'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email'
                },
                {
                    name: 'web',
                    label: t('web', 'Página web'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                },
                {
                    name: 'CustomerOriginId',
                    label: t('origin', 'Origen'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictCustomerOrigins || {},
                },
                {
                    name: 'customer_type',
                    component: 'InputHidden',
                    value: 0
                },           
                {
                    name: 'createAddress',
                    component: 'CheckboxMargin',
                    label: t('createAddress', 'Crear dirección'),
                    checked: createAddress,
                    onChange: handleShowCreateAddress
                }
            ]
        },
        ...addressFields,
        {
            cols: 1,
            title: t('customerType', 'Tipo de cliente'),
            elements: [
                {
                    name: 'customersForParse',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: dictionaryComplexFromJsonArr(customerGroups?.B2CCustomerGroups || []),
                },
            ]
        }
    ]
    return (
        <ModalCreateEdit fields={fields} url={'/api/app/customer/createCustomer'} translations={{
        title: t('createCustomer', 'Crear cliente'),
        button: t('create', 'Crear')
        }} haveButtonPermissions={true} {...rest} />
    )
}
interface ModalCreateCustomerLogicProps {
    employees: EmployeesPayload
}
const useMocalCreateCustomerLogic = ({ employees }: ModalCreateCustomerLogicProps) => {
    const [dictCustomerOrigins, setDictCustomerOrigins] = useState<TwinDictionary | null>(null)
    const [createAddress, setCreateAddress] = useState(true)

    const myEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}))), [employees])

    const getDictCustomerOrigins = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerOrigin/getAllCustomerOrigins', {})
        if (result) {
            setDictCustomerOrigins(dictionaryComplexFromJsonArr(result))
        }
    }, [setDictCustomerOrigins])

    const handleShowCreateAddress = useCallback(() => {
        setCreateAddress((old)=>!old)
    }, [setCreateAddress])

    useEffect(() => {
        getDictCustomerOrigins()
    }, [getDictCustomerOrigins])

    return { dictCustomerOrigins, handleShowCreateAddress, createAddress, myEmployees }
}

const modalCreateCustomerDispatch = {
    setCustomerGroups: (payload: CustomerGroupPayload) => ({ type: 'CHANGE_CUSTOMER_GROUP', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxModalCreateCustomer = ConnectedProps<typeof ModalCreateCustomerConnect>
const mapModalCreateCustomerConnector = (state: AllReduxPayloads) => ({ customerGroups: state.customerGroups, employees: state.employees })
const ModalCreateCustomerConnect = connect(mapModalCreateCustomerConnector, modalCreateCustomerDispatch)

const modalCreateCustomerConnectLoading = withLoading(ModalCreateCustomer, [{ fetchUrl: '/api/app/customer/customerGroup/getAllCustomersGroup', propName: 'customerGroups', setFunctionName: 'setCustomerGroups' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])

export default ModalCreateCustomerConnect(modalCreateCustomerConnectLoading);
